/**
 * PlaybookRunDetail - Comprehensive viewer for a single playbook execution.
 *
 * ## Playbook Execution Runtime (PER) Architecture
 *
 * This page visualizes the output of PER, the durable orchestration system for playbook execution.
 * Understanding PER's concepts helps interpret what this page displays:
 *
 * ### Execution Model
 * - **PlaybookRun**: A single execution instance of a playbook definition
 * - **ChatSession**: The conversation context where messages are exchanged (1:1 with PlaybookRun)
 * - **Frame**: One uninterrupted LLM inference loop (may include multiple tool calls)
 *
 * ### Nesting & Hierarchy
 * Playbooks can spawn child playbooks via the `executePlaybook` tool:
 * - `parentPlaybookRunId`: Links to parent PlaybookRun (playbook → playbook nesting)
 * - `parentChatSessionId`: Links to foreground chat (user chat → playbook)
 * - `depth`: Nesting level (0 = top-level, 1+ = nested)
 *
 * ### Elicitation (User Input Requests)
 * Playbooks can pause and request user input via the `elicit` tool:
 * - `isElicitation`: Message contains an elicit tool call
 * - `elicitationResolvedBy`: FK to the user's response message
 * - Status transitions: running → awaiting_user_input → running → complete
 *
 * ### Frame IDs
 * Each frame gets a UUID persisted to messages for debugging:
 * - Groups messages from a single streamText() call
 * - A frame may encompass multiple LLM inference calls (tool use loops)
 * - New frame starts when: elicitation pauses, child playbook spawned
 *
 * @see PlaybookExecutionRuntime (applications/api) for orchestration logic
 * @see ChatMessageFragment for elicitation field definitions
 * @see PlaybookRunFragment for nesting field definitions
 */

import {
  type ChatMessageFragment,
  type PlaybookRunFragment,
  useChatSessionQuery,
  useChildPlaybookRunsQuery,
  useInboxConversationQuery,
  usePlaybookRunQuery,
} from "@qw/graphql";
import {
  Badge,
  Button,
  Clippy,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Column,
  ColumnContent,
  ColumnHeader,
  Error,
  Icon,
  type MaterialSymbol,
  Spinner,
  sanitizeHtml,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@qw/ui";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { usePage } from "../providers/use-page";

const STATUS_CONFIG: Record<
  string,
  { label: string; emoji: string; variant: "secondary" | "destructive" | "green" | "outline" }
> = {
  running: { label: "Running", emoji: "⏳", variant: "secondary" },
  awaiting_user_input: { label: "Awaiting Input", emoji: "⏸️", variant: "secondary" },
  awaiting_child_playbook: { label: "Awaiting Child", emoji: "🔄", variant: "secondary" },
  complete: { label: "Complete", emoji: "✅", variant: "green" },
  error: { label: "Failed", emoji: "❌", variant: "destructive" },
  cancelled: { label: "Cancelled", emoji: "🚫", variant: "outline" },
};

const truncateId = (id: string, length = 8) => id.slice(0, length);

// Format duration in a human-readable way
const formatDuration = (ms: number): string => {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Format token count with thousands separator
const formatTokens = (count: number): string => {
  return count.toLocaleString();
};

interface AncestorRun {
  id: string;
  playbookId: string;
  playbook?: { name: string } | null;
  status: string;
  parentPlaybookRunId?: string | null;
}

// Helper for displaying abbreviated run ID with tooltip showing full ID and copy button
const RunIdBadge = ({ id }: { id: string }) => (
  <div className="flex items-center gap-0.5">
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-xs text-muted-foreground font-mono cursor-default">{truncateId(id)}</span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-xs text-muted-foreground mb-1">Playbook Run ID</p>
        <p className="font-mono text-xs">{id}</p>
      </TooltipContent>
    </Tooltip>
    <Clippy content={id} variant="ghost" size="xs" className="h-5 w-5" />
  </div>
);

// Recursive component that fetches and displays ancestor chain
const AncestorChain = ({ parentId, depth = 0 }: { parentId: string; depth?: number }) => {
  const { data, loading } = usePlaybookRunQuery({
    variables: { id: parentId },
  });

  const parentRun = data?.playbookRun as AncestorRun | undefined;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner className="h-3 w-3" /> Loading ancestor...
      </div>
    );
  }

  if (!parentRun) {
    return null;
  }

  const parentStatus = STATUS_CONFIG[parentRun.status] ?? STATUS_CONFIG.error;

  return (
    <>
      {/* Recursively render grandparent and above first */}
      {parentRun.parentPlaybookRunId && <AncestorChain parentId={parentRun.parentPlaybookRunId} depth={depth + 1} />}
      {/* Then render this ancestor */}
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground cursor-default">↑</span>
          </TooltipTrigger>
          <TooltipContent>
            {depth === 0 ? "Parent playbook that spawned this run" : `Ancestor (${depth + 1} levels up)`}
          </TooltipContent>
        </Tooltip>
        <Link to={`/playbooks/runs/${parentRun.id}`} className="text-primary hover:underline flex items-center gap-2">
          <span className="font-medium">{parentRun.playbook?.name ?? "(untitled)"}</span>
        </Link>
        <RunIdBadge id={parentRun.id} />
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant={parentStatus.variant} className="ml-auto text-xs cursor-default">
              {parentStatus.emoji}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>Status: {parentStatus.label}</TooltipContent>
        </Tooltip>
      </div>
    </>
  );
};

// Recursive component that fetches and displays descendant tree
const DescendantTree = ({ parentId, depth = 0 }: { parentId: string; depth?: number }) => {
  const { data, loading } = useChildPlaybookRunsQuery({
    variables: { parentPlaybookRunId: parentId },
  });

  const children = data?.childPlaybookRuns ?? [];

  if (loading) {
    return (
      <div className="ml-4 flex items-center gap-2 text-muted-foreground">
        <Spinner className="h-3 w-3" /> Loading children...
      </div>
    );
  }

  if (children.length === 0) {
    return null;
  }

  return (
    <>
      {children.map((child) => {
        const childStatus = STATUS_CONFIG[child.status] ?? STATUS_CONFIG.error;
        return (
          <div key={child.id} className="mt-3 ml-4">
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-muted-foreground cursor-default">└─</span>
                </TooltipTrigger>
                <TooltipContent>
                  {depth === 0 ? "Child playbook spawned by this run" : `Descendant (${depth + 1} levels down)`}
                </TooltipContent>
              </Tooltip>
              <Link to={`/playbooks/runs/${child.id}`} className="text-primary hover:underline flex items-center gap-2">
                <span className="font-medium">{child.playbook?.name ?? "(untitled)"}</span>
              </Link>
              <RunIdBadge id={child.id} />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant={childStatus.variant} className="ml-auto text-xs cursor-default">
                    {childStatus.emoji}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Status: {childStatus.label}</TooltipContent>
              </Tooltip>
            </div>
            {/* Recursively render grandchildren */}
            <DescendantTree parentId={child.id} depth={depth + 1} />
          </div>
        );
      })}
    </>
  );
};

interface HierarchySectionProps {
  run: {
    id: string;
    playbook?: { name: string } | null;
    parentPlaybookRunId?: string | null;
    depth?: number | null;
  };
  status: { label: string; emoji: string; variant: "secondary" | "destructive" | "green" | "outline" };
  hasParent: boolean;
}

// Wrapper component that only shows hierarchy if there are ancestors or descendants
const HierarchySection = ({ run, status, hasParent }: HierarchySectionProps) => {
  // Check if this run has any children
  const { data: childrenData, loading: childrenLoading } = useChildPlaybookRunsQuery({
    variables: { parentPlaybookRunId: run.id },
  });

  const hasChildren = (childrenData?.childPlaybookRuns?.length ?? 0) > 0;

  // Don't render if no parent and no children (and not loading)
  if (!hasParent && !hasChildren && !childrenLoading) {
    return null;
  }

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <h2 className="text-sm font-semibold text-muted-foreground cursor-default">Hierarchy</h2>
        </TooltipTrigger>
        <TooltipContent>Parent/child playbook run relationships</TooltipContent>
      </Tooltip>

      {/* Ancestors (recursive) */}
      {hasParent && <AncestorChain parentId={run.parentPlaybookRunId!} />}

      {/* Current (highlighted) */}
      <div className="flex items-center gap-2 bg-muted/50 rounded px-2 py-1 -mx-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-primary font-bold cursor-default">●</span>
          </TooltipTrigger>
          <TooltipContent>
            Current playbook run (you are here)
            {run.depth !== null && run.depth !== undefined && run.depth > 0 && ` • Depth: ${run.depth}`}
          </TooltipContent>
        </Tooltip>
        <span className="font-medium">{run.playbook?.name ?? "(untitled)"}</span>
        <RunIdBadge id={run.id} />
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant={status.variant} className="ml-auto text-xs cursor-default">
              {status.emoji}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>Status: {status.label}</TooltipContent>
        </Tooltip>
      </div>

      {/* Descendants (recursive) - reuse the already-fetched data */}
      {hasChildren &&
        childrenData?.childPlaybookRuns?.map((child) => {
          const childStatus = STATUS_CONFIG[child.status] ?? STATUS_CONFIG.error;
          return (
            <div key={child.id} className="mt-3 ml-4">
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground cursor-default">└─</span>
                  </TooltipTrigger>
                  <TooltipContent>Child playbook spawned by this run</TooltipContent>
                </Tooltip>
                <Link
                  to={`/playbooks/runs/${child.id}`}
                  className="text-primary hover:underline flex items-center gap-2"
                >
                  <span className="font-medium">{child.playbook?.name ?? "(untitled)"}</span>
                </Link>
                <RunIdBadge id={child.id} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant={childStatus.variant} className="ml-auto text-xs cursor-default">
                      {childStatus.emoji}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Status: {childStatus.label}</TooltipContent>
                </Tooltip>
              </div>
              {/* Recursively render grandchildren */}
              <DescendantTree parentId={child.id} depth={1} />
            </div>
          );
        })}

      {/* Loading state for children check */}
      {childrenLoading && !hasParent && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Spinner className="h-3 w-3" /> Checking for children...
        </div>
      )}
    </div>
  );
};

// Helper component for copyable IDs (shows abbreviated UUID with tooltip for full value)
const IdRow = ({ label, value, link }: { label: string; value?: string | null; link?: string }) => {
  if (!value) {
    return null;
  }
  const abbreviated = truncateId(value);
  return (
    <div className="flex items-center gap-1">
      <span className="text-sm text-muted-foreground">{label}:</span>
      <Tooltip>
        <TooltipTrigger asChild>
          {link ? (
            <Link to={link} className="font-mono text-xs text-primary hover:underline">
              {abbreviated}
            </Link>
          ) : (
            <span className="font-mono text-xs cursor-default">{abbreviated}</span>
          )}
        </TooltipTrigger>
        <TooltipContent className="font-mono text-xs">{value}</TooltipContent>
      </Tooltip>
      <Clippy content={value} variant="ghost" size="xs" className="h-5 w-5" />
    </div>
  );
};

type TriggerType = "event_trigger" | "nested_playbook" | "foreground_chat" | "background";

const getTriggerType = (run: PlaybookRunFragment): TriggerType => {
  if (run.parentPlaybookRunId) {
    return "nested_playbook";
  }
  if (run.parentChatSessionId) {
    return "foreground_chat";
  }
  if (run.triggerEventType) {
    return "event_trigger";
  }
  return "background";
};

// Human-readable labels for trigger event types
const TRIGGER_EVENT_LABELS: Record<string, { label: string; description: string; icon: MaterialSymbol }> = {
  "Schedule.weekdays": {
    label: "Weekday schedule",
    description: "Triggered by a weekday schedule in Central (Chicago) time",
    icon: "schedule",
  },
  "Opportunity.assignedToBroker": {
    label: "Opportunity Assignment",
    description: "Triggered when an opportunity was assigned to a broker",
    icon: "assignment_ind",
  },
  "Email.received": {
    label: "Email Received",
    description: "Triggered when a new email was received in the inbox",
    icon: "mail",
  },
};

const TriggerSourceSection = ({ run }: { run: PlaybookRunFragment }) => {
  const triggerType = getTriggerType(run);
  const eventConfig = run.triggerEventType ? TRIGGER_EVENT_LABELS[run.triggerEventType] : null;

  return (
    <div className="rounded-lg border p-4">
      <Tooltip>
        <TooltipTrigger asChild>
          <h2 className="text-sm font-semibold text-muted-foreground mb-3 cursor-default">Trigger Source</h2>
        </TooltipTrigger>
        <TooltipContent>How this playbook run was initiated</TooltipContent>
      </Tooltip>

      {triggerType === "event_trigger" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon icon={eventConfig?.icon ?? "bolt"} className="text-muted-foreground" />
            <span className="font-medium">{eventConfig?.label ?? run.triggerEventType}</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {eventConfig?.description ?? `Triggered by event: ${run.triggerEventType}`}
          </p>
          {/* Show full event type (not truncated like UUIDs) */}
          <div className="flex items-center gap-1">
            <span className="text-sm text-muted-foreground">Event Type:</span>
            <span className="font-mono text-xs">{run.triggerEventType}</span>
            <Clippy content={run.triggerEventType ?? ""} variant="ghost" size="xs" className="h-5 w-5" />
          </div>
          {run.opportunityId && <IdRow label="Opportunity" value={run.opportunityId} />}
        </div>
      )}

      {triggerType === "background" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon icon="schedule" className="text-muted-foreground" />
            <span className="font-medium">Background / API</span>
          </div>
          <p className="text-sm text-muted-foreground">Triggered programmatically (API call or background job)</p>
        </div>
      )}

      {triggerType === "nested_playbook" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon icon="account_tree" className="text-muted-foreground" />
            <span className="font-medium">Nested Playbook</span>
          </div>
          <p className="text-sm text-muted-foreground">Spawned by parent playbook via executePlaybook tool</p>
          <IdRow label="Tool Invocation" value={run.toolInvocationId} />
        </div>
      )}

      {triggerType === "foreground_chat" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Icon icon="chat" className="text-muted-foreground" />
            <span className="font-medium">User Chat</span>
          </div>
          <p className="text-sm text-muted-foreground">Spawned from interactive chat via executePlaybook tool</p>
          <IdRow label="Parent Session" value={run.parentChatSessionId} />
          <IdRow label="Tool Invocation" value={run.toolInvocationId} />
        </div>
      )}
    </div>
  );
};

const PlaybookPromptCard = ({ run }: { run: PlaybookRunFragment }) => {
  const [isOpen, setIsOpen] = useState(false);
  const workflowContent = run.playbook?.workflow || "";

  // Check if parameterValues exist and have content
  const parameterValues = run.parameterValues as Record<string, unknown> | null;
  const hasParameterValues = parameterValues && Object.keys(parameterValues).length > 0;

  if (!workflowContent && !hasParameterValues) {
    return null;
  }

  return (
    <div className="rounded-lg border p-3 bg-muted/50">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Icon icon="terminal" className="text-muted-foreground" />
        <span className="text-sm font-medium">System</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className="text-[10px]">
              Playbook Prompt
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            The playbook prompt is generated dynamically and may have changed since this run
          </TooltipContent>
        </Tooltip>
        {hasParameterValues && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-[10px]">
                + Parameters
              </Badge>
            </TooltipTrigger>
            <TooltipContent>This run received parameter values via executePlaybook</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Collapsible content */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <Icon icon={isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"} />
            <span>{isOpen ? "Hide" : "Show"} prompt content</span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-3">
            {/* Parameter Values - injected at runtime */}
            {hasParameterValues && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon icon="data_object" className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Parameter Values</span>
                </div>
                <pre className="text-xs bg-muted/30 rounded p-2 overflow-x-auto max-h-32 overflow-y-auto">
                  {JSON.stringify(parameterValues, null, 2)}
                </pre>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Injected into the system prompt when this playbook was invoked
                </p>
              </div>
            )}

            {/* Workflow content */}
            {workflowContent && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon icon="description" className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Workflow Instructions</span>
                </div>
                <div
                  className="text-sm bg-muted/30 rounded p-3 overflow-x-auto max-h-64 overflow-y-auto prose prose-sm prose-neutral dark:prose-invert max-w-none [&_table]:border-collapse [&_table]:w-full [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(workflowContent) }}
                />
              </div>
            )}

            <div className="text-[11px] text-muted-foreground space-y-1 pt-2 border-t border-muted">
              <p className="font-medium">About this section:</p>
              <ul className="list-disc list-inside space-y-0.5 ml-1">
                <li>
                  <strong>Parameter values</strong> are the actual values passed when this run was invoked
                </li>
                <li>
                  <strong>Workflow instructions</strong> reflect the current playbook config (may have changed since
                  execution)
                </li>
                <li>
                  The full system prompt also includes: conversation context, execution guidelines, and attached files
                </li>
              </ul>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// Execution metrics section showing duration and token usage
const ExecutionMetricsSection = ({ run }: { run: PlaybookRunFragment }) => {
  // Calculate duration
  const durationMs =
    run.startedAt && run.finishedAt ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime() : null;

  // Get token data directly from PlaybookRun (aggregated from AiInteraction records)
  const inputTokens = run.totalInputTokens ?? 0;
  const outputTokens = run.totalOutputTokens ?? 0;
  const totalTokens = inputTokens + outputTokens;
  const hasTokenData = inputTokens > 0 || outputTokens > 0;
  const aiCallCount = run.aiCallCount ?? 0;

  return (
    <div className="rounded-lg border p-4">
      <Tooltip>
        <TooltipTrigger asChild>
          <h2 className="text-sm font-semibold text-muted-foreground mb-3 cursor-default">Execution Metrics</h2>
        </TooltipTrigger>
        <TooltipContent>Performance and resource usage for this playbook run</TooltipContent>
      </Tooltip>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {/* Duration */}
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1 cursor-default">
                <Icon icon="timer" className="h-4 w-4" />
                <span className="text-xs font-medium">Duration</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Total execution time from start to finish</TooltipContent>
          </Tooltip>
          <p className="text-lg font-semibold">
            {durationMs !== null ? formatDuration(durationMs) : run.finishedAt ? "—" : "Running..."}
          </p>
        </div>

        {/* AI Calls */}
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1 cursor-default">
                <Icon icon="psychology" className="h-4 w-4" />
                <span className="text-xs font-medium">AI Calls</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Number of LLM API calls made during this run</TooltipContent>
          </Tooltip>
          <p className="text-lg font-semibold">{aiCallCount}</p>
        </div>

        {/* Total Tokens */}
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1 cursor-default">
                <Icon icon="token" className="h-4 w-4" />
                <span className="text-xs font-medium">Total Tokens</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Combined input and output tokens used by the LLM</TooltipContent>
          </Tooltip>
          <p className="text-lg font-semibold">{hasTokenData ? formatTokens(totalTokens) : "—"}</p>
        </div>

        {/* Input Tokens */}
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1 cursor-default">
                <Icon icon="input" className="h-4 w-4" />
                <span className="text-xs font-medium">Input</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Tokens sent to the LLM (prompts, context)</TooltipContent>
          </Tooltip>
          <p className="text-lg font-semibold text-blue-600">{hasTokenData ? formatTokens(inputTokens) : "—"}</p>
        </div>

        {/* Output Tokens */}
        <div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1 cursor-default">
                <Icon icon="output" className="h-4 w-4" />
                <span className="text-xs font-medium">Output</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>Tokens generated by the LLM (responses)</TooltipContent>
          </Tooltip>
          <p className="text-lg font-semibold text-green-600">{hasTokenData ? formatTokens(outputTokens) : "—"}</p>
        </div>
      </div>
    </div>
  );
};

const formatJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

interface ToolInvocation {
  id: string;
  toolCallId?: string | null;
  toolName: string;
  status: string;
  calledAt: string;
  finishedAt?: string | null;
  toolInput: unknown;
  toolOutput?: unknown | null;
}

// Message part types
interface MessagePart {
  type: string;
  text?: string;
  url?: string;
  toolName?: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  output?: unknown;
}

const parseMessageParts = (partsJson: string): MessagePart[] => {
  try {
    return JSON.parse(partsJson) as MessagePart[];
  } catch {
    return [];
  }
};

const MessagePartDisplay = ({ part }: { part: MessagePart }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (part.type === "text" && part.text) {
    return <p className="whitespace-pre-wrap">{part.text}</p>;
  }

  if (part.type === "reasoning" && part.text) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <Icon icon="psychology" className="text-muted-foreground" />
            <span>Reasoning</span>
            <Icon icon={isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-1 rounded border bg-muted/30 p-2 text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
            {part.text}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  // Tool invocations (tool-elicit, tool-executePlaybook, etc.)
  if (part.type?.startsWith("tool-")) {
    const toolName = part.type.replace("tool-", "");

    const handleClick = () => {
      // Try toolCallId first, fall back to searching by tool name if not available
      const targetId = part.toolCallId ? `tool-${part.toolCallId}` : null;
      const el = targetId ? document.getElementById(targetId) : null;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Fun animation: single pulse with scale bounce + glow (optimistic green)
        el.style.transition = "transform 0.2s ease, box-shadow 0.3s ease";
        el.style.transform = "scale(1.02)";
        el.style.boxShadow = "0 0 40px 12px rgba(34, 197, 94, 0.7)";
        el.classList.add("ring-2", "ring-green-500");
        setTimeout(() => {
          el.style.transform = "scale(1)";
          el.style.boxShadow = "";
          el.classList.remove("ring-2", "ring-green-500");
        }, 400);
      }
    };

    return (
      <div className="block">
        <button
          onClick={handleClick}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <Icon icon="build" className="h-3.5 w-3.5" />
          <span className="font-medium">{toolName}</span>
          <Icon icon="arrow_forward" className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return null;
};

const MessageCard = ({ message, messages }: { message: ChatMessageFragment; messages: ChatMessageFragment[] }) => {
  const parts = parseMessageParts(message.parts);
  const isUser = message.role === "user";

  // Check if this user message is a response to an elicitation
  const isElicitationResponse = isUser && messages.some((m) => m.elicitationResolvedBy === message.id);

  return (
    <div className={`rounded-lg border p-3 ${isUser ? "bg-muted/30" : "bg-background"}`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Icon icon={isUser ? "person" : "smart_toy"} className="text-muted-foreground" />
        <span className="text-sm font-medium">{isUser ? "User" : "Assistant"}</span>

        {/* Elicitation badge */}
        {message.isElicitation && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant={message.elicitationResolvedBy ? "green" : "secondary"} className="text-xs">
                {message.elicitationResolvedBy ? "✅ Elicitation Resolved" : "⏸️ Elicitation Pending"}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {message.elicitationResolvedBy ? "This elicitation has been resolved" : "Waiting for user response"}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Elicitation response badge */}
        {isElicitationResponse && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs">
                💬 Response
              </Badge>
            </TooltipTrigger>
            <TooltipContent>This message is a response to an elicitation</TooltipContent>
          </Tooltip>
        )}

        {/* Frame ID */}
        {message.frameId && (
          <div className="flex items-center gap-0.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground font-mono cursor-default">
                  {truncateId(message.frameId)}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs text-muted-foreground mb-1">Frame ID — groups messages from one execution loop</p>
                <p className="font-mono text-xs">{message.frameId}</p>
              </TooltipContent>
            </Tooltip>
            <Clippy content={message.frameId} variant="ghost" size="xs" className="h-5 w-5" />
          </div>
        )}

        <span className="ml-auto text-xs text-muted-foreground">
          {new Date(message.createdAt).toLocaleTimeString()}
        </span>
      </div>

      {/* Message parts */}
      <div className="space-y-2 text-sm">
        {parts.map((part, idx) => (
          <MessagePartDisplay key={idx} part={part} />
        ))}
      </div>
    </div>
  );
};

/** Check if tool output indicates an error (e.g., failed executePlaybook) */
const isToolOutputError = (toolOutput: unknown): boolean => {
  if (!toolOutput || typeof toolOutput !== "object") {
    return false;
  }
  const output = toolOutput as Record<string, unknown>;
  // Check structuredContent.result.data.status === "error"
  const structuredContent = output.structuredContent as Record<string, unknown> | undefined;
  const result = structuredContent?.result as Record<string, unknown> | undefined;
  const data = result?.data as Record<string, unknown> | undefined;
  return data?.status === "error";
};

const ToolInvocationCard = ({ invocation: inv }: { invocation: ToolInvocation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasDetails = Boolean(inv.toolInput || inv.toolOutput);
  const duration =
    inv.finishedAt && inv.calledAt
      ? Math.round((new Date(inv.finishedAt).getTime() - new Date(inv.calledAt).getTime()) / 1000)
      : null;
  const hasOutputError = isToolOutputError(inv.toolOutput);
  const isError = inv.status === "error" || hasOutputError;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div id={`tool-${inv.toolCallId}`} className="rounded border overflow-hidden transition-all duration-300">
        <CollapsibleTrigger asChild disabled={!hasDetails}>
          <button
            className={`w-full p-3 flex items-center gap-3 text-left ${
              hasDetails ? "hover:bg-muted/50 cursor-pointer" : ""
            }`}
            title={hasDetails ? "Click to expand details" : undefined}
          >
            <Icon
              icon={isError ? "error" : inv.status === "running" ? "pending" : "check_circle"}
              className={
                isError
                  ? "text-destructive"
                  : inv.status === "running"
                    ? "text-yellow-500 animate-spin"
                    : "text-green-500"
              }
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">{inv.toolName}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span>{new Date(inv.calledAt).toLocaleString()}</span>
                {duration !== null && <span>({duration}s)</span>}
              </div>
            </div>
            {hasDetails && (
              <Icon icon={isOpen ? "keyboard_arrow_up" : "keyboard_arrow_down"} className="text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t p-3 space-y-3 bg-muted/30">
            {inv.toolInput ? (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-1">Input</h4>
                <pre className="text-xs bg-background rounded p-2 overflow-x-auto max-h-48 overflow-y-auto">
                  {formatJson(inv.toolInput)}
                </pre>
              </div>
            ) : null}
            {inv.toolOutput ? (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-1">Output</h4>
                <pre className="text-xs bg-background rounded p-2 overflow-x-auto max-h-48 overflow-y-auto">
                  {formatJson(inv.toolOutput)}
                </pre>
              </div>
            ) : null}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// Section that displays messages (data passed from parent)
const MessagesSection = ({ messages, loading }: { messages: ChatMessageFragment[]; loading: boolean }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading messages...
      </div>
    );
  }

  if (messages.length === 0) {
    return <p className="text-muted-foreground">No messages recorded.</p>;
  }

  return (
    <div className="space-y-3">
      {messages.map((msg) => (
        <MessageCard key={msg.id} message={msg} messages={messages} />
      ))}
    </div>
  );
};

export const PlaybookRunDetail = () => {
  const { playbookRunId } = useParams<{ playbookRunId: string }>();
  const { rootPath } = usePage();

  const { data, loading, error } = usePlaybookRunQuery({
    variables: { id: playbookRunId! },
    skip: !playbookRunId,
  });

  const run = data?.playbookRun;

  // Fetch chat session data for messages and stats
  const { data: chatSessionData, loading: chatSessionLoading } = useChatSessionQuery({
    variables: { id: run?.chatSessionId ?? "" },
    skip: !run?.chatSessionId,
  });

  const messages = chatSessionData?.chatSession?.messages ?? [];

  // Fetch conversation data to get insuredId
  const { data: conversationData } = useInboxConversationQuery({
    variables: { input: { conversationId: run?.conversationId ?? "" } },
    skip: !run?.conversationId,
  });

  // Extract insuredId from chat session metadata OR conversation
  const metadata = chatSessionData?.chatSession?.metadata ?? [];
  const insuredIdFromMetadata = metadata.find((m) => m.key === "insuredId")?.value ?? null;
  const insuredIdFromConversation = conversationData?.inboxConversation?.insuredId ?? null;
  const insuredId = insuredIdFromConversation || insuredIdFromMetadata;

  // Compute statistics
  const messageCount = messages.length;
  const toolCallCount = run?.toolInvocations?.length ?? 0;
  const distinctFrameIds = new Set(messages.map((m) => m.frameId).filter(Boolean));
  const frameCount = distinctFrameIds.size;

  if (loading) {
    return (
      <Column className="flex-auto bg-background">
        <div className="flex-auto flex items-center justify-center">
          <Spinner />
        </div>
      </Column>
    );
  }

  if (error) {
    return <Error error={error} className="p-4" />;
  }

  if (!run) {
    return (
      <Column className="flex-auto bg-background">
        <ColumnContent className="p-4">
          <div className="text-center text-muted-foreground">Playbook run not found</div>
        </ColumnContent>
      </Column>
    );
  }

  const status = STATUS_CONFIG[run.status] ?? STATUS_CONFIG.error;
  const hasParent = !!run.parentPlaybookRunId;

  return (
    <TooltipProvider>
      <Column className="flex-auto bg-background">
        <ColumnHeader className="px-4 py-2">
          <Link to="/playbooks/runs">
            <Button variant="ghost" size="sm">
              <Icon icon="arrow_back" /> Back to Runs
            </Button>
          </Link>
        </ColumnHeader>
        <ColumnContent className="p-4">
          <div className="max-w-4xl space-y-6">
            {/* Run Details */}
            <div className="rounded-lg border p-4">
              {/* Header row with playbook name and status */}
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-xl font-semibold">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to={`/playbooks/${run.playbookId}`} className="text-primary hover:underline">
                        {run.playbook?.name ?? "Playbook Run"}
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>View playbook definition</TooltipContent>
                  </Tooltip>
                </h1>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant={status.variant} className="cursor-default">
                      {status.emoji} {status.label}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Current execution status</TooltipContent>
                </Tooltip>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Run ID</h3>
                  <div className="flex items-center gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="font-mono cursor-default">{truncateId(run.id)}</span>
                      </TooltipTrigger>
                      <TooltipContent className="font-mono text-xs">{run.id}</TooltipContent>
                    </Tooltip>
                    <Clippy content={run.id} variant="ghost" size="xs" className="h-6 w-6" title="Copy run ID" />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Chat Session</h3>
                  {run.chatSessionId ? (
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link to={`/chat/${run.chatSessionId}`} className="text-primary hover:underline font-mono">
                            {truncateId(run.chatSessionId)}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent className="font-mono text-xs">{run.chatSessionId}</TooltipContent>
                      </Tooltip>
                      <Clippy
                        content={run.chatSessionId}
                        variant="ghost"
                        size="xs"
                        className="h-6 w-6"
                        title="Copy chat session ID"
                      />
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">User</h3>
                  <p>
                    {run.user ? (
                      <span>
                        {run.user.firstName} {run.user.lastName}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Started At</h3>
                  <p>{new Date(run.startedAt).toLocaleString()}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Finished At</h3>
                  <p>{run.finishedAt ? new Date(run.finishedAt).toLocaleString() : "—"}</p>
                </div>
              </div>

              {/* Context Links */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Conversation</h3>
                  {run.conversationId ? (
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link
                            to={{ pathname: rootPath, search: `?conversationId=${run.conversationId}` }}
                            className="text-primary hover:underline font-mono"
                          >
                            {truncateId(run.conversationId)}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent className="font-mono text-xs">{run.conversationId}</TooltipContent>
                      </Tooltip>
                      <Clippy
                        content={run.conversationId}
                        variant="ghost"
                        size="xs"
                        className="h-6 w-6"
                        title="Copy conversation ID"
                      />
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Insured</h3>
                  {insuredId ? (
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link to={`${rootPath}/${insuredId}`} className="text-primary hover:underline font-mono">
                            {truncateId(insuredId)}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent className="font-mono text-xs">{insuredId}</TooltipContent>
                      </Tooltip>
                      <Clippy
                        content={insuredId}
                        variant="ghost"
                        size="xs"
                        className="h-6 w-6"
                        title="Copy insured ID"
                      />
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
              </div>

              {/* Summary Statistics */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-6 text-sm">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-default">
                        <Icon icon="stacks" className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{run.depth ?? 0}</span>
                        <span className="text-muted-foreground">depth</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Nesting level (0 = top-level, 1+ = nested playbook)</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-default">
                        <Icon icon="build" className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{toolCallCount}</span>
                        <span className="text-muted-foreground">tool calls</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Number of MCP tool invocations during this run</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-default">
                        <Icon icon="chat" className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{chatSessionLoading ? "—" : messageCount}</span>
                        <span className="text-muted-foreground">messages</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Number of messages in the chat session</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5 cursor-default">
                        <Icon icon="layers" className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{chatSessionLoading ? "—" : frameCount}</span>
                        <span className="text-muted-foreground">frames</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm">
                      <p className="mb-2">
                        A frame represents one uninterrupted execution loop. Within a single frame, the LLM may call
                        tools and receive their results multiple times—this back-and-forth continues until the model
                        produces a final response or hits a stopping condition.
                      </p>
                      <p>
                        A new frame begins whenever execution must pause and yield control: when waiting for user input
                        via elicitation, or when a nested playbook is spawned and the parent must wait for it to
                        complete. Most simple playbook runs have just one frame.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Hierarchy - shows ancestors and descendants recursively */}
            <HierarchySection run={run} status={status} hasParent={hasParent} />

            {/* Trigger Source */}
            <TriggerSourceSection run={run} />

            {/* Execution Metrics */}
            <ExecutionMetricsSection run={run} />

            {/* Error message */}
            {run.errorMessage && (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
                <h3 className="font-medium text-destructive mb-1">Error</h3>
                <pre className="text-sm whitespace-pre-wrap">{run.errorMessage}</pre>
              </div>
            )}

            {/* Tool Invocations */}
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <h2 className="text-lg font-semibold mb-3 cursor-default">Tool Invocations</h2>
                </TooltipTrigger>
                <TooltipContent>MCP tools called during playbook execution (click to expand)</TooltipContent>
              </Tooltip>
              {run.toolInvocations.length === 0 ? (
                <p className="text-muted-foreground">No tool calls recorded.</p>
              ) : (
                <div className="space-y-2">
                  {run.toolInvocations.map((inv) => (
                    <ToolInvocationCard key={inv.id} invocation={inv} />
                  ))}
                </div>
              )}
            </div>

            {/* Messages */}
            <div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <h2 className="text-lg font-semibold mb-3 cursor-default">Messages</h2>
                </TooltipTrigger>
                <TooltipContent>Chat messages from this playbook run's session</TooltipContent>
              </Tooltip>
              <div className="space-y-3">
                {/* Playbook Prompt as System message */}
                <PlaybookPromptCard run={run} />
                {/* Chat messages */}
                {run.chatSessionId ? (
                  <MessagesSection messages={messages} loading={chatSessionLoading} />
                ) : (
                  <p className="text-muted-foreground">No chat session associated with this run.</p>
                )}
              </div>
            </div>
          </div>
        </ColumnContent>
      </Column>
    </TooltipProvider>
  );
};
